import { defineVars } from '@stylexjs/stylex';
import { productAccents } from './palette.stylex';

/** One color and themed control-image contract for the whole UI; add semantic roles here as components migrate. */
export const colors = defineVars({
  derivedTreeConnector: '#d3d3d3',
  selectedSearchItemBorder: '#d3d3d3',
  derivedTreeFallbackIcon: '#808080',
  dataPanelSelectionBorder: '#f9bd4b',
  dataPanelInvalidFocusHalo: '#eba5a3',
  dataPanelUsageIcon: '#606061',
  dataPanelBorder: '#e6e7e8',
  visualizationRangeHover: '#f1f5f9',
  tablePropertyHover: '#eeeeee',
  tableAnalysisHelpText: '#427c63',
  visualizationMenuChevron: '#e2e2e2',
  trendSummaryCopyBackground: '#ffffff',
  // Preserve the legacy panel palette; capsule uncertainty has a dark override.
  detailsMutedText: '#999',
  capsuleUncertainText: '#b7b7b7',
  propertySelectorSeparator: '#e6e7e8',
  knowledgeActive: '#fcb426',
  // The legacy --sq-background-primary was undefined: background-color computed to transparent.
  knowledgeSurfaceBackground: 'transparent',
  knowledgeToggleBackground: 'rgba(0, 0, 0, 0.14)',
  knowledgeToggleBorder: 'rgba(0, 0, 0, 0.14)',
  knowledgeToggleShadow: 'inset 0 1px 1px rgba(255, 255, 255, 0.55), inset 0 -1px 1px rgba(0, 0, 0, 0.05)',
  knowledgeToggleThumbBorder: 'rgba(0, 0, 0, 0.16)',
  knowledgeToggleThumbShadow: '0 1px 2px rgba(0, 0, 0, 0.22), inset 0 1px 0 rgba(255, 255, 255, 0.8)',
  knowledgeToggleCheckedBorder: 'rgba(0, 0, 0, 0.1)',

  organizerValidationText: '#d9534f',
  organizerLoadingProgress: '#17a2b8',
  organizerFailedProgress: '#dc3545',
  organizerDateGroupBackground: 'transparent',
  organizerLegacyInputBackground: '#ffffff',
  organizerLegacyInputBorder: '#e6e7e8',
  organizerSelectedTemplate: '#f7ecb5',
  organizerSelectedTemplateHover: '#f5e69e',
  organizerValidSource: '#dff0d8',
  organizerInvalidSource: '#f2dede',
  organizerContentSeparator: '#d3d3d3',
  organizerSaveStatus: '#6c757d',

  organizerContentMenuBackground: '#ffffff',
  organizerMarkdownPanelBorder: '#e6e7e8',
  organizerDashboardTriggerText: '#3a3a3a',
  organizerDashboardTriggerHover: '#f1f5f7',
  organizerPreviewBackground: '#e7e7e7',
  organizerTemplateBorder: '#dde1e3',
  captureSuggestionText: 'var(--theme-dark)',
  headerBackground: '#2a5c84',
  headerBrandBackground: '#062f41',
  headerViewOnlyText: '#e2e2e2',
  workbookPrimary: '#2a5c84',
  worksheetThumbnailBorder: '#b7b7b7',
  worksheetAnalysisAction: '#00cb72',
  worksheetTopicAction: '#41a5e1',
  worksheetBoardAction: '#0096ff',

  // Legacy themeColors.scss roles differ from the temporary Tailwind --theme-* adapter in dark mode.
  legacyToolsGroupBorder: '#e6e7e8',
  legacyToolsPrimary: '#2a5c84',
  legacyToolsLink: '#2a5c84',
  legacyToolsRowHover: '#edf7f6',
  legacyToolsRowFocus: '#edf7f6',
  toolsDisabledBorder: '#dde1e3',
  toolPanelBorder: '#e2e2e2',
  toolTableHeaderBorder: '#f1f5f7',
  toolTableRowHover: '#f1f5f7',
  toolsSearchHighlight: '#fde1ad',
  formGroupHeadingBackground: '#f9f9f9',

  formulaUnselectedBackground: '#dde1e3',
  formulaTypeBadgeBackground: '#f1f5f7',

  legacyInfoBackground: '#fff3cd',
  legacyInfoBorder: '#ffc107',
  statisticsDetailsBackground: 'rgba(207, 201, 202, 0.1)',

  capsuleModeBackground: '#ffffff',
  capsuleModeBorder: 'oklch(87.2% 0.01 258.338)',
  piVisionFieldBackground: '#eeedee',
  piVisionFieldBorder: '#dde1e3',
  toolSelectedRow: '#eef8f4',
  sampleInputBorder: '#b7b7b7',

  modelPropertyDisabled: '#cccccc',
  modelPropertyBorder: '#dee2e6',
  modelPropertyHeading: '#007960',
  toolThemeLink: 'var(--theme-link)',
  toolThemeDark: 'var(--theme-dark)',
  toolThemeLight: 'var(--theme-light)',
  toolRowHover: 'var(--theme-item-row)',
  templateOutputBorder: 'oklch(87.2% 0.01 258.338)',
  templateBreakingBackground: 'color-mix(in oklab, #fff3cd 40%, transparent)',
  templateRestrictionError: 'oklch(63.7% 0.237 25.331)',
  templateFindingBackground: '#f8f6f2',

  agentBuilderTextColor: 'var(--sq-text-color)',
  agentBuilderColorScheme: 'light',
  agentBuilderDateBorder: 'var(--sq-darkest-gray)',
  agentBuilderDateActiveBorder: 'var(--theme-dark)',
  agentBuilderDateFocusShadow: 'inset 0 1px 2px 0 color-mix(in srgb, var(--theme-dark) 7.5%, transparent)',

  agentBuilderPaletteBackground: 'var(--sq-background-secondary)',
  agentBuilderCardBackground: '#ffffff',
  agentBuilderCardHoverBackground: 'var(--sq-tool-btn-bg-hover)',
  agentBuilderCardIcon: 'var(--theme-dark)',
  agentBuilderPickerBackground: '#ffffff',
  agentBuilderPickerBorder: 'var(--sq-disabled-gray)',
  agentBuilderPlaceholderBackground: '#ffffff',
  agentBuilderPlaceholderHover: '#ffffff',
  agentBuilderEdgeBackground: '#ffffff',
  agentBuilderEdgeBorder: 'oklch(86.9% 0.022 252.894)',
  agentBuilderEdgeForeground: 'oklch(37.2% 0.044 257.287)',
  agentBuilderHelpHover: 'var(--sq-text-color)',

  agentBuilderNodeBackground: 'var(--sq-table-highlight)',
  agentBuilderNodeBorder: '#ddd',
  agentBuilderNodeMuted: '#666',
  agentBuilderNodeText: '#333',
  agentBuilderNodeMeta: '#999',
  agentBuilderHandleLabel: '#555',
  agentBuilderBadgeBorder: 'var(--sq-tool-btn-border)',

  treeBuilderTabHover: 'var(--theme-light)',
  treeBuilderAssistantBackground: 'color-mix(in oklab, var(--sq-topic-color) 8%, white)',
  treeBuilderCardSelectedBackground: 'color-mix(in oklab, var(--sq-topic-color) 8%, white)',
  timelineItemKind: productAccents.topicSubtle,
  progressSuccess: '#28a745',
  vantageActiveButtonBackground: '#ffffff',
  vantageActiveButtonHover: '#f1f5f7',
  vantageActiveButtonPressed: '#e2e2e2',
  vantageActiveButtonBorder: '#dde1e3',
  vantageActiveButtonFocusBorder: productAccents.topicLight,
  vantageActiveButtonText: '#3a3a3a',
  vantageSelectedTabIcon: 'currentColor',
  // Monitor status badges retain their legacy green/red/yellow/gray palette in both light and dark mode.
  // These status colors are shared by every product theme; darkPalette does not override them.
  monitorActiveBackground: 'oklch(96.2% 0.044 156.743)',
  monitorActiveText: 'oklch(44.8% 0.119 151.328)',
  monitorErrorBackground: 'oklch(93.6% 0.032 17.717)',
  monitorErrorText: 'oklch(44.4% 0.177 26.899)',
  monitorSuppressedBackground: 'oklch(97.3% 0.071 103.193)',
  monitorSuppressedText: 'oklch(47.6% 0.114 61.907)',
  monitorInactiveBackground: 'oklch(96.7% 0.003 264.542)',
  monitorInactiveText: 'oklch(27.8% 0.033 256.848)',
  monitorErrorAction: 'oklch(57.7% 0.245 27.325)',
  monitorPlaceholderText: 'oklch(70.7% 0.022 261.325)',
  monitorOverlayText: 'oklch(55.1% 0.027 264.364)',
  evidenceFilterOptionHover: 'oklch(98.5% 0.002 247.839)',
  contextHintText: 'oklch(44.6% 0.03 256.802)',
  suppressionDivider: 'oklch(87.2% 0.01 258.338)',
  reasonCodeRequiredText: '#ff0000',
  notificationFilterBorder: 'oklch(92.8% 0.006 264.531)',
  notificationGroupBorder: 'oklch(87.2% 0.01 258.338)',
  // Vantage surfaces use these light defaults and the shared darkPalette overrides in themes.ts.
  // Every product dark theme includes those overrides, including embedded Vantage views.
  vantageRowHover: '#eeedee',
  contextLabelBackground: '#f1f5f7',
  vantageSettingsHover: '#e2e8f0',
  managementPageBackground: '#e7e7e7',
  vantageWarningBackground: '#fff3cd',
  vantageWarningBorder: '#ffecb5',
  vantageStandardBorder: 'currentColor',
  vantageToolbarBackground: 'transparent',
  vantageChartWarningBackground: 'transparent',
  vantageChartWarningText: 'inherit',
  contextFlagIcon: '#b85559',
  suppressionComparatorBorder: '#dde1e3',
  suppressionComparatorHover: '#f1f5f7',
  suppressionComparatorPressed: '#e2e2e2',
  suppressionComparatorSelectedText: '#ffffff',
  suppressionComparatorFocusBorder: productAccents.topicLight,
  suppressionComparatorSelected: productAccents.topicLight,
  suppressionComparatorSelectedHover: productAccents.topicHighlight,
  suppressionComparatorSelectedPressed: productAccents.topicDark,
  suppressionComparatorSelectedBorder: productAccents.topicLight,
  suppressionComparatorSelectedHoverBorder: productAccents.topicHighlight,
  suppressionComparatorSelectedPressedBorder: productAccents.topicDark,
  // Run outcomes keep their original warning color in every product and mode.
  runWarningText: '#664d03',
  formulaAssistantError: '#991b1b',
  productionBadgeBackground: '#28a745',
  agentVersionMenuBorder: '#e2e8f0',
  agentVersionMenuBackground: '#ffffff',

  workspaceSurface: '#ffffff',
  workspaceBorder: '#e6e7e8',
  workspaceFrameBorder: 'transparent',
  workspaceRowBorder: '#e6ecf2',
  workspaceRowBackground: productAccents.topicSubtle,
  workspaceIcon: productAccents.topicLight,
  workspaceDangerBackground: '#f2dede',

  assistantPanelBorder: '#e6e7e8',
  gaugeTrack: '#e7e7e7',
  composerPlaceholder: '#9ca3af',
  composerActionBackground: '#ffffff',
  agentDescription: '#7ea6d6',
  subduedText: '#677b7b',
  effortTrack: '#f1f5f7',
  effortSelected: '#ffffff',
  effortHover: '#dde1e3',
  guidedAccent: '#0284c7',
  guidedAccentHover: '#0369a1',
  guidedSelection: '#3b82f6',
  guidedSelectionText: '#60a5fa',
  inputBackground: '#ffffff',
  formGroupBorder: '#cccccc',
  configurationGroupBorder: '#e2e2e2',
  diffAdded: 'oklch(96.2% 0.044 156.743)',
  diffRemoved: 'oklch(93.6% 0.032 17.717)',
  historyNumber: 'oklch(92.8% 0.006 264.531)',
  historyNumberSelected: 'oklch(44.3% 0.11 240.79)',
  historyNumberText: '#ffffff',

  selectedFilterBackground: '#e2e2e2',
  readOnlyBackground: '#f5f5f5',
  readOnlyBorder: '#dde1e3',
  statusGood: '#0c684c',
  statusProgress: '#3a3a3a',
  historyWarning: 'oklch(66.6% 0.179 58.318)',
  popoverHeading: '#f1f3f4',
  separator: '#e6e7e8',
  info: '#0491c2',
  listRowAlternate: '#f1f5f7',
  readOnlyText: '#677b7b',
  subtleBorder: '#dde1e3',
  cardBorder: 'rgb(0 0 0 / 12.5%)',
  cardHeaderBackground: 'rgb(0 0 0 / 3%)',
  sidebarBorder: '#e2e2e2',
  sidebarBackground: '#f1f5f7',
  sidebarHover: '#f1f5f7',
  sidebarSelected: '#eef6fd',
  sidebarSelectedBorder: '#2a5c84',
  accent: productAccents.topicLight,
  paginationSelected: productAccents.topicLight,
  accentHover: productAccents.topicHighlight,
  accentPressed: productAccents.topicDark,
  swatchBorder: 'oklch(37.3% 0.034 259.733)',
  menuBorder: '#dde1e3',
  menuText: '#3a3a3a',
  menuTextDisabled: '#dde1e3',
  menuActionDisabled: '#dde1e3',
  notificationDanger: '#dc3545',
  dashboardTileBorder: '#e2e2e2',
  errorIconBackground: 'oklch(97.1% 0.013 17.38)',
  errorIconBorder: 'oklch(88.5% 0.062 18.334)',
  assistantActionBorder: '#a9cce6',
  assistantActionStart: '#e8f3fc',
  assistantActionEnd: '#f2f8fd',
  assistantActionText: '#0266b8',
  assistantActionIcon: '#0275d1',
  overlayPanelBackground: 'rgb(255 255 255 / 15%)',
  overlayPanelBorder: 'rgb(0 0 0 / 8%)',
  overlayPanelShadow: 'rgb(0 0 0 / 6%)',
  overlayPanelHighlight: 'transparent',
  boardTileBackground: 'transparent',
  boardTileBorder: '#e5e7eb',
  boardTileShadow: 'rgb(100 100 111 / 15%)',
  boardTileLoadingOverlay: 'rgb(255 255 255 / 45%)',
  menuHover: '#f9f9f9',
  trackBackground: '#e2e2e2',
  selectionBorder: '#e2e8f0',
  selectionBackground: '#eef6fd',
  selectionText: '#000000',
  panelBorder: '#e2e8f0',
  panelBackground: '#ffffff',
  black: '#000000',
  mutedText: '#869298',
  badgeNewBackground: '#fce7ce',
  badgeNewText: '#664d03',
  badgePositiveBackground: '#c5edd4',
  badgePositiveText: '#007960',
  badgePositiveLabelText: '#ffffff',
  badgeNeutralBackground: '#f8f6f2',
  badgeErrorBackground: '#ffede9',
  badgeNewBorder: 'rgb(240 173 78 / 40%)',
  badgePositiveBorder: 'rgb(0 121 96 / 40%)',
  badgeNeutralBorder: 'rgb(134 146 152 / 40%)',
  badgeErrorBorder: 'rgb(217 83 79 / 40%)',
  alertBackground: `color-mix(in srgb, ${productAccents.topicHighlight} 10%, transparent)`,
  formulaErrorBackground: 'oklch(88.5% 0.062 18.334)',
  formulaErrorText: 'oklch(44.4% 0.177 26.899)',
  formulaErrorBorder: 'oklch(93.6% 0.032 17.717)',
  warningBackground: '#fff3cd',
  warningText: '#3a3a3a',
  toolbarHover: '#f1f3f4',
  toolbarSelected: '#eeedee',
  toolbarSelectedPressed: '#eeedee',
  surfaceHover: '#f1f5f7',
  surfaceSelected: '#dde1e3',
  surfacePressed: '#e2e2e2',
  navigationTextActive: productAccents.topicLight,
  dangerHover: '#c9302c',
  warningHover: '#ec971f',
  accentSubtle: productAccents.topicSubtle,
  accentHighlightBackground: productAccents.topicSubtle,
  link: productAccents.topicLight,
  white: '#ffffff',
  bodyText: '#3a3a3a',
  textOnLight: '#3a3a3a',
  tooltipBackground: '#000000',
  segmentBorderInactive: '#dde1e3',
  segmentBackground: '#ffffff',
  segmentHover: '#f1f5f7',
  segmentSelected: '#d9ecff',
  segmentSelectedHover: '#c7e3ff',
  segmentBorder: '#1f5f9f',
  tabBorder: '#eeedee',
  tabHover: '#f1f5f7',
  tabLabel: productAccents.topicLight,
  tabLabelActive: 'oklch(55.1% 0.027 264.364)',
  modalBackdrop: '#0d1117',
  modalBorder: 'transparent',
  checkboxBorder: '#dde1e3',
  checkboxImage:
    "url(\"data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'%3e%3cpath fill='%23000000' d='M12.207 4.793a1 1 0 010 1.414l-5 5a1 1 0 01-1.414 0l-2-2a1 1 0 011.414-1.414L6.5 9.086l4.293-4.293a1 1 0 011.414 0z'/%3e%3c/svg%3e\")",
  checkboxDisabledImage:
    "url(\"data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'%3e%3cpath fill='%23606061' d='M12.207 4.793a1 1 0 010 1.414l-5 5a1 1 0 01-1.414 0l-2-2a1 1 0 011.414-1.414L6.5 9.086l4.293-4.293a1 1 0 011.414 0z'/%3e%3c/svg%3e\")",
  checkboxIndeterminateImage:
    "url(\"data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'%3e%3cpath stroke='%23000000' stroke-width='2' stroke-linecap='round' d='M4 8h8'/%3e%3c/svg%3e\")",
  checkboxDisabledIndeterminateImage:
    "url(\"data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'%3e%3cpath stroke='%23606061' stroke-width='2' stroke-linecap='round' d='M4 8h8'/%3e%3c/svg%3e\")",
  textMuted: '#b7b7b7',
  secondaryText: '#6c757d',
  mutedForeground: '#606061',
  supportingForeground: '#869298',
  controlHoverBackground: '#f1f5f9',
  contrastForeground: '#000000',
  secondaryBackground: '#f7f7f8',
  contrastSelectedBackground: 'rgb(0 0 0 / 10%)',
  secondaryForeground: 'oklch(55.4% 0.046 257.417)',
  surfaceSubtle: '#f1f5f7',
  tableRowAlternate: '#f9f9f9',
  divider: '#e2e8f0',
  textSubtle: '#606061',
  iconMuted: '#dde1e3',
  warning: '#f0ad4e',
  success: '#72c02c',
  icon: productAccents.topicIcon,
  iconForeground: productAccents.topicIcon,
  background: '#ffffff',
  // Let standalone light fields inherit their containing surface.
  fieldBackground: 'initial',
  backgroundDisabled: '#f1f5f7',
  border: '#dde1e3',
  borderInvalid: '#d9534f',
  borderDisabled: '#dde1e3',
  text: '#3a3a3a',
  textDisabled: '#b7b7b7',
  placeholder: 'oklch(70.7% 0.022 261.325)',
  itemSelectionPlaceholder: 'rgb(156 163 175)',
  borderHover: productAccents.topicLight,
  borderFocus: productAccents.topicLight,
  borderActive: productAccents.topicLight,
});
